import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { SUPPORTED_LOCALES } from '../../i18n'

const LOCALE_OG: Record<string, string> = {
  en: 'en_GB', fr: 'fr_FR', de: 'de_DE',
}

interface SEOProps {
  title: string
  description: string
  canonical?: string
  ogImage?: string
  ogType?: 'website' | 'article' | 'product'
  structuredData?: object | object[]
  noindex?: boolean
}

export default function SEO({
  title,
  description,
  canonical,
  ogImage = '/og-image.png',
  ogType = 'website',
  structuredData,
  noindex = false
}: SEOProps) {
  const siteUrl = 'https://carbon13.studio'
  const { lang } = useParams<{ lang: string }>()
  const currentLang = lang || 'en'
  const fullTitle = title.includes('Carbon13') ? title : `${title} | Carbon13`
  const pathWithoutLang = canonical?.replace(/^\/[a-z]{2}(\/|$)/, '/') || '/'
  const canonicalUrl = `${siteUrl}/${currentLang}${pathWithoutLang === '/' ? '' : pathWithoutLang}`

  useEffect(() => {
    document.title = fullTitle

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name'
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement
      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute(attr, name)
        document.head.appendChild(meta)
      }
      meta.content = content
    }

    setMeta('description', description)
    setMeta('og:title', fullTitle, true)
    setMeta('og:description', description, true)
    setMeta('og:type', ogType, true)
    setMeta('og:image', `${siteUrl}${ogImage}`, true)
    setMeta('og:site_name', 'Carbon13', true)
    setMeta('og:locale', LOCALE_OG[currentLang] || 'en_GB', true)
    setMeta('og:url', canonicalUrl, true)
    setMeta('twitter:card', 'summary_large_image')
    setMeta('twitter:title', fullTitle)
    setMeta('twitter:description', description)
    setMeta('twitter:image', `${siteUrl}${ogImage}`)

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
    if (!link) {
      link = document.createElement('link')
      link.rel = 'canonical'
      document.head.appendChild(link)
    }
    link.href = canonicalUrl

    const existingAlts = document.querySelectorAll('link[data-seo="hreflang"]')
    existingAlts.forEach(el => el.remove())

    for (const loc of SUPPORTED_LOCALES) {
      const alt = document.createElement('link')
      alt.rel = 'alternate'
      alt.hreflang = loc
      alt.href = `${siteUrl}/${loc}${pathWithoutLang === '/' ? '' : pathWithoutLang}`
      alt.setAttribute('data-seo', 'hreflang')
      document.head.appendChild(alt)
    }
    const xDefault = document.createElement('link')
    xDefault.rel = 'alternate'
    xDefault.hreflang = 'x-default'
    xDefault.href = `${siteUrl}/en${pathWithoutLang === '/' ? '' : pathWithoutLang}`
    xDefault.setAttribute('data-seo', 'hreflang')
    document.head.appendChild(xDefault)

    if (noindex) {
      setMeta('robots', 'noindex, nofollow')
    } else {
      setMeta('robots', 'index, follow')
    }

    const existingScripts = document.querySelectorAll('script[data-seo="structured-data"]')
    existingScripts.forEach(s => s.remove())

    if (structuredData) {
      const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData]
      dataArray.forEach(data => {
        const script = document.createElement('script')
        script.type = 'application/ld+json'
        script.setAttribute('data-seo', 'structured-data')
        script.textContent = JSON.stringify(data)
        document.head.appendChild(script)
      })
    }

    return () => {
      const scripts = document.querySelectorAll('script[data-seo="structured-data"]')
      scripts.forEach(s => s.remove())
      const alts = document.querySelectorAll('link[data-seo="hreflang"]')
      alts.forEach(el => el.remove())
    }
  }, [fullTitle, description, canonicalUrl, ogImage, ogType, structuredData, noindex, currentLang, pathWithoutLang])

  return null
}

export const createOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Carbon13',
  url: 'https://carbon13.studio',
  description: 'Environmental consultancy specialising in carbon credit projects, MRV services, and sustainability documentation.',
})

export const createFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
})

export const createBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `https://carbon13.studio${item.url}`
  }))
})
